import { Menu, Text } from '@mantine/core';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import TreeNodeOptions from '@/components/shared/TreeNodeOptions';
import FieldCheckbox from '@/components/shared/form/FieldCheckbox';
import {
  TREE_ROOT_NODE_ID,
  TreeNodeFolder,
  isInsidePersonalWorkspace,
  isOrganizationRootPersonalFolder,
} from '@/entities/Tree';
import { PopupStore } from '@/stores/local/shared/PopupStore';
import { TreeStore } from '@/stores/local/shared/TreeStore';
import { useSidebarDocumentsStore } from '@/stores/local/sidebar/SidebarDocumentsStore';
import { CreateFolderPopupStore } from '@/stores/local/sidebar/SidebarDocumentsStore/PopupsStore/CreateFolderPopupStore';
import { navigateToChatWithSession } from '@/utils/chatNavigation';
import { useDevices } from '@/utils/devices';

import s from './FolderActions.module.scss';

const DROPDOWN_ITEM_CLASS_NAME = s['root__dropdown-item'];

type Props = {
  deletePopupStore: PopupStore<TreeNodeFolder>;
  editFolderPopupStore: CreateFolderPopupStore;
  uploadDocumentPopup: PopupStore<TreeNodeFolder | null>;
  model: TreeNodeFolder;
  treeStore: TreeStore;
  withSelection?: boolean;
};

const FolderActionsComponent: React.FC<Props> = ({
  deletePopupStore,
  model,
  editFolderPopupStore,
  uploadDocumentPopup,
  treeStore,
  withSelection,
}: Props) => {
  const { isCompactViewport } = useDevices();
  const navigate = useNavigate();
  const { id } = useParams();

  const { permissionsStore, selectionStore, searchDocumentStore } = useSidebarDocumentsStore();
  const folderId = model.id;

  const parentId = treeStore.parent?.data.id ?? null;
  const shouldValidateCreate = model.id !== TREE_ROOT_NODE_ID;
  const shouldValidateRename = !(parentId === null || parentId === TREE_ROOT_NODE_ID);
  const parentFolder = treeStore.parent?.data?.type === 'folder' ? (treeStore.parent.data as TreeNodeFolder) : null;

  const canEdit = permissionsStore.canEdit(folderId, model.type);
  const canCreate = permissionsStore.canCreate();
  const canCreateInPersonal = permissionsStore.canCreateInPersonal();
  const inPersonalWorkspace = isInsidePersonalWorkspace(treeStore);
  const canDeleteOrgFolder = permissionsStore.canDelete(folderId, model.type);
  const isRootPersonalFolder = isOrganizationRootPersonalFolder(treeStore, model);
  const canDelete =
    model.id !== TREE_ROOT_NODE_ID && !isRootPersonalFolder && (inPersonalWorkspace || canDeleteOrgFolder);
  const canCreateFolder = inPersonalWorkspace ? canCreateInPersonal : canCreate && canEdit;
  const canUploadDocument = permissionsStore.canUpload(folderId, model.type);
  const canRename = !isRootPersonalFolder && (inPersonalWorkspace || canEdit);
  const hasFolderActions = canCreateFolder || canUploadDocument || canRename || canDelete;
  const isMenuDisabled = permissionsStore.meta.isLoading || !hasFolderActions;
  const menuTooltip = isMenuDisabled
    ? permissionsStore.meta.isLoading
      ? 'Загружаю права доступа...'
      : 'Недостаточно прав для действий с этой папкой'
    : undefined;

  const handleStopPropagation = React.useCallback((event: React.MouseEvent<HTMLInputElement | HTMLButtonElement>) => {
    event.stopPropagation();
  }, []);

  const handleOpenDelete = React.useCallback(() => {
    deletePopupStore.open(model);
  }, [deletePopupStore, model]);

  const handleCreateFolder = React.useCallback(() => {
    if (!canCreateFolder) {
      return;
    }

    editFolderPopupStore.openForCreate(model.id, {
      requireDateFormat: shouldValidateCreate && !inPersonalWorkspace,
      parent: model,
    });
  }, [canCreateFolder, editFolderPopupStore, inPersonalWorkspace, model, shouldValidateCreate]);

  const handleEdit = React.useCallback(() => {
    if (!canRename) {
      return;
    }

    editFolderPopupStore.openForEdit(model, parentId, {
      requireDateFormat: shouldValidateRename && !inPersonalWorkspace,
      parent: parentFolder,
    });
  }, [canRename, editFolderPopupStore, inPersonalWorkspace, model, parentFolder, parentId, shouldValidateRename]);

  const handleOpenUpload = React.useCallback(() => {
    if (!canUploadDocument) {
      return;
    }

    uploadDocumentPopup.open(model);
  }, [canUploadDocument, model, uploadDocumentPopup]);

  const checkboxState = withSelection ? selectionStore.getNodeState(treeStore) : null;

  const handleToggleSelection = React.useCallback(() => {
    selectionStore.toggleNode(treeStore);
  }, [selectionStore, treeStore]);

  const handleDiscussWithAi = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (!id) {
        return;
      }

      selectionStore.selectOnly(treeStore);
      searchDocumentStore.selectionStore.clear();
      navigateToChatWithSession({ navigate, organizationId: id });
    },
    [id, navigate, searchDocumentStore.selectionStore, selectionStore, treeStore],
  );

  const rootClassName = cn(s.root, withSelection ? s['root_three-columns'] : s['root_two-columns']);

  if (isCompactViewport) {
    return (
      <div className={rootClassName}>
        <div className={s.root__slot} />
        <div className={s.root__slot}>
          <TreeNodeOptions
            triggerLabel="Обсудить с ИИ"
            items={
              <Menu.Item onClick={handleDiscussWithAi}>
                <Text className={DROPDOWN_ITEM_CLASS_NAME} size="sm">
                  Обсудить с ИИ
                </Text>
              </Menu.Item>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className={rootClassName}>
      <div className={s.root__slot} />
      <div className={s.root__slot}>
        <TreeNodeOptions
          disabled={isMenuDisabled}
          disabledTooltip={menuTooltip}
          items={
            <>
              <Menu.Item onClick={handleCreateFolder} disabled={!canCreateFolder}>
                <Text className={DROPDOWN_ITEM_CLASS_NAME} size="sm">
                  Создать папку
                </Text>
              </Menu.Item>
              {!isRootPersonalFolder && (
                <Menu.Item onClick={handleEdit} disabled={!canRename}>
                  <Text className={DROPDOWN_ITEM_CLASS_NAME} size="sm">
                    Переименовать
                  </Text>
                </Menu.Item>
              )}
              <Menu.Item onClick={handleOpenUpload} disabled={!canUploadDocument}>
                Загрузить документы
              </Menu.Item>
              {canDelete && (
                <Menu.Item onClick={handleOpenDelete} className={s.root__remove}>
                  <Text className={DROPDOWN_ITEM_CLASS_NAME} size="sm" c="red">
                    Удалить
                  </Text>
                </Menu.Item>
              )}
            </>
          }
        />
      </div>
      {withSelection ? (
        <div className={cn(s.root__slot, s.root__slot_checkbox)}>
          {checkboxState ? (
            <FieldCheckbox
              checked={checkboxState.checked}
              indeterminate={checkboxState.indeterminate}
              onToggle={handleToggleSelection}
              onClick={handleStopPropagation}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default observer(FolderActionsComponent);
